import { greetingSlotForDate, type TimeOfDay } from "@healthos/shared";
import { useEffect, useState } from "react";

/**
 * Local time-of-day slot. Drives the ambient palette via
 * `data-time-of-day` on the AppShell wrapper, which feeds the
 * `--ambient-blob-*-color` custom properties consumed by
 * `bg-ambient-mesh`.
 *
 * Slot boundaries are intentionally coarse (4 slots) so the canvas
 * doesn't visibly shift every hour. Within a slot the canvas is
 * stable; AT a boundary the blob colours cross-fade over 4s
 * (transition declared in `base.css`) so the user feels a slow
 * palette dissolve rather than a snap.
 *
 *   05:00 -> 12:00  morning   (warmer; teal-300 + day-dawn)
 *   12:00 -> 17:00  afternoon (brand; teal-600 + leaf)
 *   17:00 -> 21:00  evening   (cooler; teal-700 + day-twilight)
 *   21:00 -> 05:00  night     (deepest; teal-800 + day-dusk)
 *
 * The slot rule itself lives in `greetingSlotForDate` in
 * `@healthos/shared` so the mobile greeting composer and this hook
 * cannot drift. Only the React scheduling (resync on mount, setTimeout
 * to the next boundary) stays here.
 *
 * Implementation note: we do NOT use `setInterval`. We compute the
 * exact ms until the next boundary and `setTimeout` once, then
 * reschedule from inside the handler. This keeps the page idle for
 * hours at a stretch, which matters because the AppShell mounts
 * persist for the entire session.
 */

export type { TimeOfDay };

const BOUNDARY_HOURS = [5, 12, 17, 21] as const;
const DEFAULT_SLOT: TimeOfDay = "afternoon";

function msUntilNextBoundary(now: Date): number {
  const target = new Date(now);
  target.setMinutes(0, 0, 0);
  const hour = now.getHours();
  const next = BOUNDARY_HOURS.find((b) => b > hour);
  if (next === undefined) {
    // Past the last boundary of today; next is the first boundary tomorrow.
    target.setHours(BOUNDARY_HOURS[0]);
    target.setDate(target.getDate() + 1);
  } else {
    target.setHours(next);
  }
  return target.getTime() - now.getTime();
}

export function useTimeOfDay(): TimeOfDay {
  // SSR returns the default; the post-mount effect immediately
  // resyncs to the real local hour.
  const [slot, setSlot] = useState<TimeOfDay>(() => {
    if (typeof window === "undefined") return DEFAULT_SLOT;
    return greetingSlotForDate(new Date());
  });

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const schedule = () => {
      if (cancelled) return;
      const now = new Date();
      const delay = msUntilNextBoundary(now);
      // Tiny buffer past the boundary so we read the new hour
      // (avoids a clock-drift race where setTimeout fires a few ms
      // early and `getHours()` still reports the old slot).
      timeoutId = setTimeout(() => {
        if (cancelled) return;
        setSlot(greetingSlotForDate(new Date()));
        schedule();
      }, delay + 250);
    };

    // Resync in case SSR returned the default and the real hour differs.
    setSlot(greetingSlotForDate(new Date()));
    schedule();

    return () => {
      cancelled = true;
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
  }, []);

  return slot;
}
